import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { Scale, Plus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mockCourtDates, type CourtDate } from './mockData';

function CourtDateRow({ courtDate }: { courtDate: CourtDate }) {
  const navigate = useNavigate();
  const now = new Date();
  const daysUntil = Math.ceil(
    (courtDate.date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  const urgencyBg =
    daysUntil <= 3
      ? 'bg-red-50 hover:bg-red-100/70'
      : daysUntil <= 7
        ? 'bg-amber-50 hover:bg-amber-100/70'
        : 'hover:bg-slate-50';

  // Extract short case name (e.g., "Schmidt" from "Schmidt vs. Lufthansa")
  const shortCaseName = courtDate.caseTitle.split(' vs.')[0] || courtDate.caseTitle;

  // Shorten court name (e.g., "AG Frankfurt" from "Amtsgericht Frankfurt")
  const shortCourtName = courtDate.courtName
    .replace('Amtsgericht', 'AG')
    .replace('Landgericht', 'LG');

  return (
    <TableRow
      onClick={() => navigate({ to: `/lawyer/cases/${courtDate.caseRef.split('-').pop()}` })}
      className={cn('cursor-pointer transition-colors', urgencyBg)}
    >
      {/* Date + Time */}
      <TableCell className="py-2">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-slate-900">
            {format(courtDate.date, 'd.MM', { locale: de })}
          </span>
          <span className="text-xs text-slate-500">
            {format(courtDate.date, 'HH:mm')}
          </span>
        </div>
      </TableCell>

      {/* Case */}
      <TableCell className="py-2">
        <div className="flex flex-col">
          <span className="text-sm text-slate-900 truncate max-w-[120px]">
            {shortCaseName}
          </span>
          <span className="text-xs font-mono text-slate-500">
            {courtDate.caseRef}
          </span>
        </div>
      </TableCell>

      {/* Court + Room */}
      <TableCell className="py-2">
        <div className="flex flex-col">
          <span className="text-sm text-slate-700">{shortCourtName}</span>
          <span className="text-xs text-slate-500">{courtDate.room}</span>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function CourtDateList() {
  // Filter and sort upcoming court dates (next 30 days)
  const upcomingCourtDates = useMemo(() => {
    const now = new Date();
    const thirtyDaysFromNow = addDays(now, 30);

    return mockCourtDates
      .filter((cd) => isAfter(cd.date, now) && isBefore(cd.date, thirtyDaysFromNow))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.3 }}
      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden w-fit min-w-[320px]"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-purple-600" />
          <h2 className="font-semibold text-slate-900 text-sm">Gerichtstermine</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 gap-1 px-2"
          onClick={() => {
            // TODO: Open add court date modal
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Hinzufügen
        </Button>
      </div>

      {/* Table */}
      {upcomingCourtDates.length === 0 ? (
        <div className="text-center py-6">
          <Scale className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Keine anstehenden Gerichtstermine</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="py-2 text-xs w-16">Datum</TableHead>
              <TableHead className="py-2 text-xs">Fall</TableHead>
              <TableHead className="py-2 text-xs">Gericht</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {upcomingCourtDates.map((courtDate) => (
              <CourtDateRow key={courtDate.id} courtDate={courtDate} />
            ))}
          </TableBody>
        </Table>
      )}
    </motion.div>
  );
}
